import { ApInvoiceTransactionStatusEnum } from '@/context/services/transaction_items/List/TransactionItems.service';
import {
  isAuthError,
  isForbiddenError,
  isInvalidError,
  isSuccess,
} from '@/context/services/type';
import { useQueryClient } from '@tanstack/react-query';
import {
  CommonError,
  getGetApInvoiceTransactionGeneralInputQueryKey,
  PutApInvoiceTransactionGeneralInputMutationBody,
  PutApInvoiceTransactionGeneralInputMutationError,
  PutApInvoiceTransactionGeneralInputMutationResult,
  UnauthorizedResponse,
  usePutApInvoiceTransactionGeneralInput,
} from 'ap-openapi';
import { useCallback } from 'react';

export type UpdateApInvoiceTransactionGeneralInputUnprocessableEntityResponse =
  {
    errors: {
      [key: string]: {
        message: string;
        type: string;
      };
    };
  };

type TUpdateTransactionItem = ReturnType<
  typeof usePutApInvoiceTransactionGeneralInput
>['mutateAsync'];
type UpdateTransactionItemMutateArgs = Parameters<TUpdateTransactionItem>['0'];

type UpdateApInvoiceTransactionGeneralInputSuccess = {
  status: ApInvoiceTransactionStatusEnum.SUCCESS;
};

type UpdateApInvoiceTransactionGeneralInputAuthError = {
  status: ApInvoiceTransactionStatusEnum.AUTH_ERROR;
  errors: UnauthorizedResponse;
  redirectPath: string;
};

type UpdateApInvoiceTransactionGeneralInputInvalidError = {
  status: ApInvoiceTransactionStatusEnum.INVALID_ERROR;
  errors: UpdateApInvoiceTransactionGeneralInputUnprocessableEntityResponse;
};

type UpdateApInvoiceTransactionGeneralInputCommonError = {
  status: ApInvoiceTransactionStatusEnum.COMMON_ERROR;
  errors: CommonError;
};

type UpdateApInvoiceTransactionGeneralInputResult =
  | UpdateApInvoiceTransactionGeneralInputSuccess
  | UpdateApInvoiceTransactionGeneralInputAuthError
  | UpdateApInvoiceTransactionGeneralInputInvalidError
  | UpdateApInvoiceTransactionGeneralInputCommonError;

export const useUpdateTransactionItem = (id: string) => {
  const queryClient = useQueryClient();
  const { mutateAsync: _mutateAsync } = usePutApInvoiceTransactionGeneralInput({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(
          getGetApInvoiceTransactionGeneralInputQueryKey(id),
          data
        );
      },
    },
  });

  const mutateAsync = useCallback(
    async (
      args: UpdateTransactionItemMutateArgs
    ): Promise<UpdateApInvoiceTransactionGeneralInputResult> => {
      const resp = await _mutateAsync(args);

      if (isSuccess(resp.status)) {
        return {
          status: ApInvoiceTransactionStatusEnum.SUCCESS,
        };
      } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
        return {
          status: ApInvoiceTransactionStatusEnum.AUTH_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as UnauthorizedResponse,
          redirectPath: resp.headers?.location ?? '/',
        };
      } else if (isInvalidError(resp.status)) {
        return {
          status: ApInvoiceTransactionStatusEnum.INVALID_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ??
            {}) as UpdateApInvoiceTransactionGeneralInputUnprocessableEntityResponse,
        };
      } else {
        return {
          status: ApInvoiceTransactionStatusEnum.COMMON_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as CommonError,
        };
      }
    },
    [_mutateAsync]
  );

  return { mutateAsync };
};

export type {
  PutApInvoiceTransactionGeneralInputMutationBody,
  PutApInvoiceTransactionGeneralInputMutationError,
  PutApInvoiceTransactionGeneralInputMutationResult,
  UpdateApInvoiceTransactionGeneralInputAuthError,
  UpdateApInvoiceTransactionGeneralInputCommonError,
  UpdateApInvoiceTransactionGeneralInputInvalidError,
  UpdateApInvoiceTransactionGeneralInputResult,
  UpdateTransactionItemMutateArgs,
};
